// ============================================================================
// WEDDING CARDS CONFIGURATION
// ============================================================================
// This file contains configurations for multiple wedding events.
// Currently, only CARD_1 (Reception) is active in the application.
// CARD_2 (Sangeet Night) is kept for reference and will be used when
// cloning this repo for a separate domain/subdomain.
// ============================================================================

// Shared details that can be used across cards
export interface SharedWeddingDetails {
  brideName: string;
  groomName: string;
  groomFatherName: string;
  groomMotherName: string;
  venueName: string;
  venueAddress: string;
}

// Card-specific configuration
export interface CardConfig {
  id: string;
  route: string;
  googleSheetName: string;
  googleAppsScriptUrl: string;
  storageKey: string;
  
  // Event details
  eventType: string;
  weddingDate: Date;
  weddingDateDisplay: string;
  weddingTime: string;
  weddingDay: string;
  dressCode: string;
  
  // Bride's Parents Information (card-specific)
  brideFatherName?: string;
  brideMotherName?: string;
  showBrideParents: boolean;
  
  // Card-specific options
  organizationOptions: string[];
  invitationFromOptions: string[];
  
  // Schedule (can be different per card)
  schedule: Array<{ time: string; event: string }>;
  
  // Media (card-specific)
  videoPath: string;
  audioPath: string;
  
  // Shared details reference
  sharedDetails: SharedWeddingDetails;
}

// Shared wedding details
export const SHARED_DETAILS: SharedWeddingDetails = {
  brideName: "Zakiah",
  groomName: "Muhammad Azmir",
  groomFatherName: "Dato' Sri Dr. Mohammed Azman bin Aziz Mohammed",
  groomMotherName: "⁠Datin Sri Wahinah binti Haroon Rasheed",
  venueName: "Menara Kembar Bank Rakyat, Dewan Tun Abdul Razak",
  venueAddress: "No. 33, Jalan Rakyat, Kuala Lumpur Sentral, 50470, Wilayah Persekutuan Kuala Lumpur.",
};

// Card 1 Configuration - Reception (21 Dec 2025)
export const CARD_1: CardConfig = {
  id: 'reception',
  route: '/reception',
  googleSheetName: 'Reception',
  googleAppsScriptUrl: 'https://script.google.com/macros/s/AKfycbxVZwq7rt2jFMr4qvctxLK9JphFUrBtjZtQ9YvpkqVV7CTijtZLTEc_egYYgQVlqOPqmg/exec',
  storageKey: 'wedding_rsvps_reception',
  
  // Event details
  eventType: "Undangan Perkahwinan",
  weddingDate: new Date('2025-12-21T11:30:00'),
  weddingDateDisplay: "21 Disember 2025",
  weddingTime: "11.30 AM - 4.00 PM",
  weddingDay: "Ahad",
  dressCode: "Formal / Tradisional",
  
  // Bride's Parents (shown in Card 1)
  brideFatherName: "Haji Abdul Rahim bin Haji Abdul Gani",
  brideMotherName: "Hajah Patma Bebe binti Haji Abdul Gani",
  showBrideParents: true,
  
  organizationOptions: ["Family", "Friends", "Staff"],
  invitationFromOptions: [
    "Dato' Sri Dr. Mohammed Azman",
    "⁠Datin Sri Wahinah",
    "Muhammad Azmir & Zakiah"
  ],
  
  schedule: [
    { time: "11:30 AM", event: "Kehadiran Tetamu" },
    { time: "12:30 PM", event: "Ketibaan Pengantin" },
    { time: "1:00 PM", event: "Santapan Beradab" },
    { time: "4:00 PM", event: "Majlis Berakhir" },
  ],
  
  // Media
  videoPath: "/videos/opening.mp4",
  audioPath: "/audio/wedding-song.mp3",
  
  sharedDetails: SHARED_DETAILS,
};

// ============================================================================
// CARD 2: SANGEET NIGHT / BOLLYWOOD NIGHT (20 DEC 2025)
// ============================================================================
// NOTE: This configuration is kept for reference and future use.
// It is NOT currently active in the application (see App.tsx).
// When cloning this repo for a separate Sangeet Night domain, activate this
// card by importing CARD_2 in App.tsx instead of CARD_1.
// Related assets:
// - Video: /public/videos/sangeet-opening.mp4
// - Audio: /public/audio/sangeet-song.mp3
// - Colors: SANGEET_COLORS in colors.ts
// ============================================================================
export const CARD_2: CardConfig = {
  id: 'sangeet',
  route: '/sangeet',
  googleSheetName: 'Sangeet',
  googleAppsScriptUrl: 'https://script.google.com/macros/s/AKfycbxVZwq7rt2jFMr4qvctxLK9JphFUrBtjZtQ9YvpkqVV7CTijtZLTEc_egYYgQVlqOPqmg/exec',
  storageKey: 'wedding_rsvps_sangeet',
  
  // Event details
  eventType: "Sangeet Night / Bollywood Night",
  weddingDate: new Date('2025-12-20T19:30:00'),
  weddingDateDisplay: "20th December 2025",
  weddingTime: "7.30 PM",
  weddingDay: "Saturday",
  dressCode: "Bollywood Attire",
  
  // Bride's Parents (not shown in Card 2)
  showBrideParents: false,
  
  organizationOptions: ["Family", "Friends", "Colleagues"],
  invitationFromOptions: [
    "Dato' Sri Dr. Mohammed Azman",
    "⁠Datin Sri Wahinah",
    "Muhammad Azmir & Zakiah"
  ],
  
  schedule: [
    { time: "7:30 PM", event: "Kehadiran Tetamu" },
    { time: "8:00 PM", event: "Acara Bermula" },
    { time: "9:00 PM", event: "Santapan & Hiburan" },
    { time: "Lewat Malam", event: "Majlis Berakhir" },
  ],
  
  // Media (different video and music for sangeet)
  videoPath: "/videos/sangeet-opening.mp4", // You'll need to add this video
  audioPath: "/audio/sangeet-song.mp3", // You'll need to add this music
  
  sharedDetails: SHARED_DETAILS,
};

// Helper to get card config by route
export const getCardConfigByRoute = (route: string): CardConfig | null => {
  if (route === CARD_1.route || route.includes('reception')) return CARD_1;
  if (route === CARD_2.route || route.includes('sangeet')) return CARD_2;
  return null;
};

// ============================================================================
// ACTIVE CONFIGURATION
// ============================================================================
// Default card points to the currently active event (Reception)
export const DEFAULT_CARD = CARD_1;

// All cards array - kept for reference
// Note: Currently only CARD_1 is used in the application
export const ALL_CARDS = [CARD_1, CARD_2];

